/**
 * StatCard component for the Indigo design system.
 *
 * A dashboard metric tile: a leading `IconBadge`, a large value, and a
 * descriptive label below it. Built for at-a-glance summary rows (e.g. the
 * physician Referral dashboard).
 *
 * Typography follows the design system:
 * - value → `Heading` at 36px
 * - label → `Heading` level 3 in the tertiary text color
 *
 * @example
 * ```tsx
 * <StatCard
 *   iconName="add-physician"
 *   iconColor="olive"
 *   value="5"
 *   label="Physicians referred"
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";
import { Card } from "../Card";
import { IconBadge, type IconBadgeProps } from "../IconBadge";
import { Heading } from "../Typography/Heading";
import "./StatCard.css";

export interface StatCardProps {
  /**
   * Name of the design-system icon shown in the leading badge.
   */
  iconName: IconBadgeProps["iconName"];

  /**
   * Color pairing for the leading icon badge.
   * @default "olive"
   */
  iconColor?: IconBadgeProps["color"];

  /**
   * The large primary value (e.g. a count or a formatted amount).
   */
  value: ReactNode;

  /**
   * Descriptive label rendered below the value.
   */
  label: ReactNode;

  /**
   * Additional CSS classes applied to the card container.
   */
  className?: string;
}

/**
 * StatCard component
 *
 * Renders a metric tile inside an elevated `Card`.
 */
export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ iconName, iconColor = "olive", value, label, className }, ref) => {
    return (
      <div ref={ref} className={clsx("indigo-stat-card", className)}>
        <Card contentClassName="indigo-stat-card__content">
          <IconBadge iconName={iconName} color={iconColor} />
          <div className="indigo-stat-card__text">
            <Heading fontSizePx={36}>{value}</Heading>
            <Heading level={3} className="text-indigo-text-tertiary">
              {label}
            </Heading>
          </div>
        </Card>
      </div>
    );
  },
);

StatCard.displayName = "StatCard";
